
import React, { useEffect, useState, useRef } from 'react'

import { SEARCH_DEBOUNCE_DELAY } from 'config'
import { useDebouncedValue } from 'hooks'
import { SearchIcon, XIcon } from 'lucide-react'

import { IconButton, Stack, useTheme } from '@mui/material'

import { Input, fieldFill } from '@repo/ui'


interface SearchProps {
  apiRef?: any
  /** When true, the consumer handles search server-side — skip the client-side
   * DataGrid quick-filter so it doesn't hide server-returned rows (e.g. custom
   * cells the quick-filter can't read). */
  serverSide?: boolean
  disabled?: boolean
  value?: string
  onChange?: (value: string) => void
  onClear?: () => void
  name?: string
  defaultValue?: string
  placeholder?: string
  fullWidth?: boolean
  debounce?: boolean
  debounceDelay?: number
  endOrdanment?: any,
  /** Show the leading magnifier. Default true. Set false for search fields INSIDE the
   *  filter drawer, where the field label already identifies it and the sibling fields
   *  are iconless (VERA-455 D — icons only on the search above the data table). */
  showIcon?: boolean
  /**
   * NOTE — the scan control does NOT live in this component (VRG-265, corrected).
   *
   * It used to render here as a trailing end-adornment (VERA-504, "at the very right of the
   * search bar input"). It now renders OUTSIDE the field, as a separate adjacent button, at
   * every surface — see `ActionLine` and `SearchFilter`. An adornment acts on a FIELD'S VALUE
   * (clear, reveal, submit) and is momentary; the scan control is a stateful MODE toggle that
   * aims an ongoing scan session at a target and stays lit. It also cannot compete with the
   * clear "×" at 16px, cannot show an active state inside the field's border without reading
   * as validation, and with a desk reader attached it is aiming a stream rather than filling
   * an input. Don't re-add an `onScan` adornment here.
   */
  sx?: object
}

//TODO: look into the proper search functionality
export const Search = ({
  apiRef,
  serverSide = false,
  disabled = false,
  value,
  onChange = () => {},
  onClear = () => {},
  name,
  defaultValue,
  fullWidth = false,
  placeholder = 'Search',
  debounce = true,
  debounceDelay = SEARCH_DEBOUNCE_DELAY,
  showIcon = true,
  sx = {},
}: SearchProps) => {
  const theme = useTheme()

  // Own the input value locally so typing is instant and is NEVER clobbered by a
  // stale debounced value. The previous implementation fired `onChange` with the
  // lagging debounced value on every keystroke, which reset the field to empty
  // (and, when the parent re-fed that as a controlled `value`, wiped what you
  // typed). The parent's `value` only seeds this and syncs external resets.
  const [inputValue, setInputValue] = useState<string>(value ?? defaultValue ?? '')
  const [debounced] = useDebouncedValue(inputValue, debounceDelay)
  const settled = debounce ? debounced : inputValue

  // Push ONLY the settled value up — this is what drives the server query. Skip
  // the initial mount so we don't fire an empty search on render.
  const mounted = useRef(false)
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
      return
    }
    onChange?.(settled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled])

  // Sync when the parent resets `value` externally (e.g. a clear elsewhere), but
  // never mid-type: only when it differs from both the live and last-pushed value.
  useEffect(() => {
    if (value !== undefined && value !== debounced && value !== inputValue) {
      setInputValue(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleClear = () => {
    setInputValue('')
    onClear()
    if (apiRef && !serverSide) {
      apiRef.current.setQuickFilterValues([])
    }
  }

  const handleChange = (value: string) => {
    setInputValue(value)
    if (apiRef && !serverSide) {
      apiRef.current.setQuickFilterValues([value])
    }
  }

  const isClearable = inputValue?.length > 0
  // TODO: not clearing input properly on X icon submit
  return (
    <Input
      value={inputValue}
      disabled={disabled}
      placeholder={placeholder}
      onChange={e => handleChange(e)}
      fullWidth={fullWidth}
      // The 24px magnifier is 1px taller than the field's 23px line, so it set the field's
      // height: 50 against every other field's 49 (VRG-703, measured on Visitor history's
      // filter row and the table toolbar, whose buttons are 49). The negative half-pixel
      // margins cancel that 1px of CONTRIBUTION without shrinking the glyph — the same move
      // as the clear button's `my: '-8px'` below.
      startAdornment={showIcon ? <SearchIcon strokeWidth={1} size={24} style={{ display: 'block', margin: '-0.5px 0' }} /> : undefined}
      endAdornment={
        <Stack direction="row" alignItems="center" sx={{ mr: -1.5 }}>
          <IconButton
            aria-label="clear search"
            onClick={() => handleClear()}
            sx={{
              display: isClearable ? 'flex' : 'none',
              // THE FIELD MUST NOT GROW WHEN YOU TYPE.
              //
              // This button is `display: none` while the field is empty and `flex` once
              // there is something to clear, so it only joins the layout mid-typing. At
              // MUI's default 8px IconButton padding around a 24px glyph its border-box
              // is 40px — taller than anything else on the row (the 24px leading
              // magnifier, the ~19px text) — so the moment it appeared it became the
              // tallest flex item and drove the field's height. Measured on the transfer
              // modal's "Transfer to" field: 50px empty, 66px with a value. Every search
              // box in the platform did this; it was simply most obvious in a modal.
              //
              // Negative vertical margins of exactly the button's own padding cancel its
              // CONTRIBUTION to the row height (outer box 24px, matching the magnifier)
              // while leaving the rendered button — and therefore the click target — a
              // full 40px. Shrinking the button instead would have fixed the height by
              // making a 24px hit area, which is the wrong trade.
              my: '-8px',
              // Small right gap so the clear "×" isn't flush against the field edge
              // (mirrors the ~13px left inset of the search icon). The negative margin
              // offsets the IconButton's own 8px padding; `edge="end"` pulled it flush.
              '&:hover': {
                  // backgroundColor: theme.palette.primary[50],
                  color: theme.palette.error.main,
              }
            }}>
             <XIcon strokeWidth={1} />
         </IconButton>
        </Stack>
      }
      sx={{
        minWidth: 300,
        // WHITE in light, against the #f6f6f6 every other field now uses. This is the one
        // documented case where the shared fill fails: fieldChrome's grey is 1.081:1 against a
        // white CARD (which is where fields normally live) but only 1.011:1 against the #f4f5f5
        // PAGE, and the table toolbar sits directly on the page — so the field all but vanished.
        // White restores the step by going the other way, which is available here precisely
        // because the backdrop is not white.
        //
        // The comment above described this and the rule itself was missing, so every search
        // box had been rendering at the shared grey.
        //
        // Written against `theme` from the closure, NOT as an `sx={theme => …}` callback:
        // the shared Input types `sx` as `object` and SPREADS it (`{...readOnlySx, ...sx}`),
        // so a callback has no enumerable properties and is silently discarded. That form
        // fails quietly — no error, no style, exactly the sort of thing that reads as "the
        // override didn't work" rather than "the override never arrived".
        //
        // Scoped to `&.MuiFilledInput-root` rather than a bare backgroundColor, because the
        // styled Input sets the fill at that specificity and a plain value loses to it.
        // `fieldFill`, NOT a hardcoded white. Search was the only field pinned to pure white, so
        // inside the Filters drawer — whose paper IS white — it had no fill at all while the date
        // field beside it sat on the shared grey. Two inputs in one panel disagreeing about what
        // an input looks like. The toolbar copy still renders white, but via ActionLine's own
        // override, which exists because that one sits on the tinted PAGE rather than on paper.
        ...(theme.palette.mode === 'light' && {
          '&.MuiFilledInput-root': {
            backgroundColor: fieldFill(theme),
            '&:hover, &.Mui-focused': { backgroundColor: fieldFill(theme) },
          },
        }),
        ...sx,
      }}
    />
  )
}